import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, InputNumber, Skeleton, Space, Typography } from 'antd';
import { ApiOutlined, ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { usePermissions } from '../../auth/store';

interface AdminOutboundHttpView {
  timeoutSec: number;
  updatedAt: string | null;
  updatedBy: string | null;
}

/** Same limits the API enforces (shared-types OUTBOUND_HTTP_LIMITS). */
export const TIMEOUT_LIMITS = { min: 1, max: 60, default: 10 } as const;

function serverError(e: unknown): string {
  const data = (e as { response?: { data?: { message?: string } } }).response?.data;
  return data?.message ?? 'Could not save the request timeout';
}

/**
 * Settings → App configuration → Third-party request timeout. How long the API
 * waits for any provider (Razorpay, Google Play, FCM, Fast2SMS, WhatsApp,
 * Truecaller, SES) before giving up, so one slow provider cannot hang requests.
 */
export function OutboundHttpCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const canEdit = can('http_timeout.edit');
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'outbound-http'],
    queryFn: async () => (await api.get<AdminOutboundHttpView>('/admin/app-config/outbound-http')).data,
  });
  const [draft, setDraft] = useState<number | null>(null);

  useEffect(() => {
    if (data && draft === null) setDraft(data.timeoutSec);
  }, [data, draft]);

  const save = useMutation({
    mutationFn: async (timeoutSec: number) =>
      (await api.patch<AdminOutboundHttpView>('/admin/app-config/outbound-http', { timeoutSec })).data,
    onSuccess: (saved) => {
      message.success(`Request timeout set to ${saved.timeoutSec}s`);
      qc.setQueryData(['admin', 'outbound-http'], saved);
      setDraft(saved.timeoutSec);
    },
    onError: (e) => message.error(serverError(e)),
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 2 }} />
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load the request timeout"
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={() => void refetch()}>
            Retry
          </Button>
        }
      />
    );
  }
  if (draft === null) return null;

  const valid = Number.isInteger(draft) && draft >= TIMEOUT_LIMITS.min && draft <= TIMEOUT_LIMITS.max;
  const dirty = draft !== data.timeoutSec;

  return (
    <Card
      title={
        <Space>
          <ApiOutlined />
          Third-party request timeout
        </Space>
      }
      extra={
        data.updatedAt ? (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Last changed {new Date(data.updatedAt).toLocaleString('en-IN')}
          </Typography.Text>
        ) : null
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%', maxWidth: 560 }}>
        <div>
          <Typography.Text>Timeout (seconds)</Typography.Text>
          <div>
            <InputNumber
              data-testid="outbound-http-timeout"
              aria-label="Timeout (seconds)"
              min={TIMEOUT_LIMITS.min}
              max={TIMEOUT_LIMITS.max}
              precision={0}
              value={draft}
              disabled={!canEdit}
              status={valid ? undefined : 'error'}
              onChange={(v) => setDraft(v ?? TIMEOUT_LIMITS.default)}
            />
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            How long the API waits for Razorpay, Google Play, FCM, Fast2SMS, WhatsApp, Truecaller or SES before
            failing the request. {TIMEOUT_LIMITS.min}–{TIMEOUT_LIMITS.max} s, default {TIMEOUT_LIMITS.default}
            . Applies to every server within about a minute.
          </Typography.Text>
        </div>
        {canEdit && (
          <Space>
            <Button
              type="primary"
              data-testid="outbound-http-save"
              disabled={!dirty || !valid}
              loading={save.isPending}
              onClick={() => save.mutate(draft)}
            >
              Save
            </Button>
            {dirty && (
              <Button onClick={() => setDraft(data.timeoutSec)} disabled={save.isPending}>
                Discard
              </Button>
            )}
          </Space>
        )}
      </Space>
    </Card>
  );
}
